/**
 * ViewManager - Manages view/navigation operations.
 *
 * Controls viewport operations like zoom, pan, background color,
 * and coordinate transformations.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DraggerManager } from "../draggers/DraggerManager";
import { DraggerType } from "../draggers/types";
import { selectionService } from "../selection";

export interface ViewManagerDeps {
  viewer: DRDrawingViewer;
  draggerManager: DraggerManager | null;
  applyOverlayTheme: (backgroundColor: number) => void;
}

export class ViewManager {
  private deps: ViewManagerDeps;

  constructor(deps: ViewManagerDeps) {
    this.deps = deps;
  }

  /**
   * Update dependencies (called when they change).
   */
  updateDeps(deps: Partial<ViewManagerDeps>): void {
    this.deps = { ...this.deps, ...deps };
  }

  /**
   * Force a render update.
   */
  update(): void {
    this.deps.viewer.update();
  }

  /**
   * Zoom to fit all entities in view.
   */
  zoomExtents(): void {
    this.deps.viewer.zoomExtents();
  }

  /**
   * Zoom to a specific window area (world coordinates).
   * @param minX Lower-left X
   * @param minY Lower-left Y
   * @param maxX Upper-right X
   * @param maxY Upper-right Y
   */
  zoomWindow(minX: number, minY: number, maxX: number, maxY: number): void {
    this.deps.viewer.zoomWindow(minX, minY, maxX, maxY);
  }

  /**
   * Activate zoom window mode.
   * User draws a rectangle to zoom to that area.
   * After zooming, returns to the previously active tool.
   */
  activateZoomWindow(): void {
    if (!this.deps.draggerManager) return;
    this.deps.draggerManager.activate(DraggerType.ZoomWindow);
  }

  /**
   * Resize the viewer to match canvas size, then tell view listeners: the
   * world→screen mapping changed (analysis frames, dragger previews…).
   */
  resize(): void {
    this.deps.viewer.resize();
    this.deps.viewer.emitViewChanged({ changeType: "resize" });
  }

  /**
   * Set the background color.
   * @param color RGB color as number (e.g., 0xFFFFFF for white, 0x000000 for black)
   */
  setBackgroundColor(color: number): void {
    this.deps.viewer.setBackgroundColor(color);
    this.deps.applyOverlayTheme(this.deps.viewer.getEffectiveBackgroundColor());
  }

  /**
   * Get the current background color property.
   */
  getBackgroundColor(): number {
    return this.deps.viewer.getBackgroundColor();
  }

  /**
   * Change the plan background while the drawing is open (display options),
   * without reopening the file (cf. DRDrawingViewer.applyBackgroundColorLive).
   * The selection is cleared first, as for a layout switch: its highlight is
   * a render state the regen may not keep.
   */
  applyBackgroundColor(color: number): void {
    selectionService.clear();
    this.deps.viewer.applyBackgroundColorLive(color);
    this.deps.applyOverlayTheme(this.deps.viewer.getEffectiveBackgroundColor());
  }

  /**
   * Convert screen coordinates to world coordinates.
   */
  screenToWorld(screen: { x: number; y: number }): { x: number; y: number } {
    return this.deps.viewer.screenToWorld(screen);
  }

  /** Projection monde → écran (pixels canvas) de la vue active. */
  worldToScreen(world: { x: number; y: number }): { x: number; y: number } {
    return this.deps.viewer.worldToScreen(world);
  }

  /**
   * Abonne aux changements de vue (pan/zoom/pinch/extents).
   * @returns fonction de désabonnement.
   */
  onViewChanged(handler: () => void): () => void {
    return this.deps.viewer.onViewChanged(handler);
  }

  regenIfNeeded(): boolean {
    return this.deps.viewer.regenIfNeeded();
  }

  forceRegen(): void {
    this.deps.viewer.forceRegen();
  }
}
